import { useMemo } from "react";
import { loadClientEnv } from "../../config/env";
import type { UIError } from "../../errors/types";

export type ErrorBannerProps = {
  error: UIError;
  onRetry?: () => void;
  onRestart?: () => void;
};

/**
 * UIError をユーザー向けに表示するバナー。
 * Requirements 6.1〜6.5: ProblemDetail を画面へ露出させず、
 * 正規化された UIError の retryable / requiresRestart に応じて操作を切り替える。
 *
 * kind ごとの特別扱いは行わず、UIError のフラグだけを見て分岐する。
 * これにより mapProblemDetail を差し替えても本コンポーネントの変更は不要になる。
 *
 * error-ux (Phase 4): debug mode (`VITE_DEBUG=true`) + friendlyErrorEnabled on のとき、
 * body の下に server 側 detail 文言を debug chip として可視化する (`aria-hidden=true`
 * で SR から除外、Req 3.1 / 5.3)。通常の運用では body はテンプレート文言のみを表示する。
 */
export function ErrorBanner({
  error,
  onRetry,
  onRestart,
}: ErrorBannerProps): JSX.Element {
  // retryable かつハンドラ存在のときのみ再試行ボタンを出す。
  // ハンドラ未指定時にボタンを残すと「押しても何も起きない」UI になり混乱を招くため。
  const showRetry = error.retryable && typeof onRetry === "function";
  const showRestart =
    error.requiresRestart && typeof onRestart === "function";

  const env = useMemo(() => loadClientEnv(), []);
  // debug chip 表示条件: debug flag on + friendlyErrorEnabled on + serverDetail が非空。
  // friendlyErrorEnabled off のとき UIError.serverDetail は populate されないが、
  // 万一他経路で値が入っても chip を出さないよう明示的に and する (Req 4.1)。
  const showDetailChip =
    env.debug === true &&
    env.friendlyErrorEnabled === true &&
    typeof error.serverDetail === "string" &&
    error.serverDetail.length > 0;

  return (
    <div
      role="alert"
      data-testid="error-banner"
      data-error-kind={error.kind}
      className="bg-reader-danger-bg border border-reader-danger-border rounded-lg p-4 text-reader-danger-fg"
    >
      <p className="font-semibold">{error.title}</p>
      <p className="text-sm text-reader-secondary mt-1">{error.body}</p>
      {showDetailChip ? (
        <p
          data-testid="error-detail-chip"
          aria-hidden="true"
          className="mt-1 text-[10px] text-reader-danger-fg font-mono opacity-60"
        >
          debug: {error.serverDetail}
        </p>
      ) : null}
      {showRetry || showRestart ? (
        <div className="flex gap-2 mt-3">
          {showRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="min-h-11 bg-reader-danger-strong hover:bg-reader-danger-strong-hover text-white rounded px-3 py-1 text-sm font-medium"
            >
              再試行
            </button>
          ) : null}
          {showRestart ? (
            <button
              type="button"
              onClick={onRestart}
              className="min-h-11 bg-transparent border border-reader-danger-border hover:bg-reader-danger-bg-hover text-reader-danger-fg rounded px-3 py-1 text-sm font-medium"
            >
              新しいプレイラインを開始
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
